/**
 * The built-in defaults (architecture doc 7.1, "Tool permissions"; tiers from ADR 0101):
 *
 *   deny   the few commands that can only destroy a machine.
 *   ask, a person   what leaves the machine or cannot be undone (default:outward): git push,
 *          publishing, deploys, ssh, writes to another service, an MCP tool that acts elsewhere.
 *          The Orgus answerer never takes these: they are the asks a person should see.
 *   ask, Orgus or a person   the grey areas: other network use (a download, a package run with
 *          npx that the project does not have), writes outside the session folder, acting in the
 *          built-in browser pane. With "Orgus answers" (ADR 0074) Orgus settles them at once.
 *   allow  reads; work inside the session folder; the local build and test loop (npx of the
 *          project's own tools, installs from the lockfile, git fetch and pull, calls to this
 *          machine); this app's own tools and any MCP tool that only reads. Reading the web
 *          (WebFetch, WebSearch) is a grey area: Orgus allows it when it answers.
 *
 * Orgus actions are allowed (the person starting them already confirmed). Every project and
 * org rule outranks these.
 */
import type { PolicyRequest, PolicyRule, PolicyResult } from '@orgus/protocol';
import { programOf, splitCommand, subcommandOf } from './command.ts';
import { isAbsolutePath, isInside } from './glob.ts';

export const SHELL_TOOLS = new Set(['bash', 'powershell']);
export const FILE_WRITE_TOOLS = new Set(['edit', 'write', 'multiedit', 'notebookedit']);
/** Tools that only read, search or plan: always fine. */
export const READ_ONLY_TOOLS = new Set([
  'read',
  'glob',
  'grep',
  'ls',
  'notebookread',
  'todowrite',
  'todoread',
  'task',
  'agent',
  'taskcreate',
  'taskupdate',
  'tasklist',
  'taskget',
  'taskoutput',
  'bashoutput',
  'exitplanmode',
  'askuserquestion',
  'toolsearch',
  'skill',
  'listmcpresourcestool',
]);
export const NETWORK_TOOLS = new Set(['webfetch', 'websearch']);

const NETWORK_PROGRAMS = new Set([
  'curl', 'wget', 'ssh', 'scp', 'sftp', 'rsync', 'nc', 'ncat', 'netcat', 'telnet', 'ftp', 'tftp',
  'invoke-webrequest', 'iwr', 'invoke-restmethod', 'irm', 'start-bitstransfer', 'gh', 'aws', 'gcloud', 'az',
  'wrangler', 'vercel', 'netlify', 'flyctl', 'fly', 'heroku', 'kubectl', 'helm', 'npx', 'pnpx', 'bunx', 'dig', 'nslookup',
]);
/** Programs whose listed subcommands touch the network. */
const NETWORK_SUBCOMMANDS: Record<string, Set<string>> = {
  git: new Set(['push', 'pull', 'fetch', 'clone', 'ls-remote', 'remote', 'submodule']),
  npm: new Set(['install', 'i', 'add', 'ci', 'publish', 'update', 'upgrade', 'exec', 'login', 'unpublish', 'dist-tag', 'audit']),
  pnpm: new Set(['install', 'i', 'add', 'publish', 'update', 'up', 'dlx', 'upgrade', 'fetch', 'audit']),
  yarn: new Set(['install', 'add', 'publish', 'upgrade', 'up', 'dlx', 'npm']),
  bun: new Set(['install', 'i', 'add', 'publish', 'update', 'x']),
  pip: new Set(['install', 'download']),
  pip3: new Set(['install', 'download']),
  uv: new Set(['pip', 'add', 'sync', 'tool']),
  pipx: new Set(['install', 'run']),
  cargo: new Set(['install', 'publish', 'add', 'fetch', 'update']),
  go: new Set(['get', 'install', 'mod']),
  gem: new Set(['install', 'push']),
  brew: new Set(['install', 'upgrade', 'update', 'tap']),
  'apt-get': new Set(['install', 'update', 'upgrade']),
  apt: new Set(['install', 'update', 'upgrade']),
  yum: new Set(['install', 'update']),
  dnf: new Set(['install', 'update']),
  choco: new Set(['install', 'upgrade']),
  winget: new Set(['install', 'upgrade']),
  scoop: new Set(['install', 'update']),
  docker: new Set(['pull', 'push', 'login', 'run', 'build']),
};
const WRITE_PROGRAMS = new Set([
  'rm', 'rmdir', 'mv', 'cp', 'tee', 'touch', 'mkdir', 'chmod', 'chown', 'ln', 'dd', 'truncate', 'install', 'unlink', 'shred',
  'remove-item', 'move-item', 'copy-item', 'set-content', 'add-content', 'out-file', 'new-item', 'rename-item', 'del', 'erase', 'rd', 'ri', 'mi', 'cpi',
]);

const URL_IN = /\b(?:https?|ftp|ssh|git|wss?):\/\/[^\s'"`<>|;)]+/g;
/** A URL on this machine: localhost, 127.x, [::1] or 0.0.0.0. */
const LOOPBACK = /^(?:https?|wss?):\/\/(?:localhost|127(?:\.\d{1,3}){3}|\[::1\]|0\.0\.0\.0)(?::\d+)?(?:[/?#]|$)/;

/** A-Z to a-z only: what a /i regex over ASCII letters sees, and nothing more. */
const asciiLower = (s: string): string => s.replace(/[A-Z]/g, (c) => c.toLowerCase());

/** The command names URLs, and every one of them is on this machine. */
function onlyLoopback(part: string): boolean {
  const urls = asciiLower(part).match(URL_IN) ?? [];
  return urls.length > 0 && urls.every((u) => LOOPBACK.test(u));
}

/** The program or its subcommand reaches the network (curl, npm install, git fetch, ...). */
function programReachesNetwork(part: string): boolean {
  const { program, args } = programOf(part);
  const p = program.toLowerCase();
  if (NETWORK_PROGRAMS.has(p)) return true;
  const subs = NETWORK_SUBCOMMANDS[p];
  if (!subs) return false;
  const sub = subcommandOf(p, args);
  return !!sub && subs.has(sub.toLowerCase());
}

const NETWORK_URL = /\b(?:https?|ftp|ssh|git):\/\//i;

function isNetworkCommand(part: string): boolean {
  return NETWORK_URL.test(part) || programReachesNetwork(part);
}

// ---- ADR 0101 tiers ----------------------------------------------------------------------

/** Programs that act on other machines or services whatever they are asked to do. */
const OUTWARD_PROGRAMS = new Set([
  'ssh', 'scp', 'sftp', 'rsync', 'nc', 'ncat', 'netcat', 'telnet', 'ftp', 'tftp', 'start-bitstransfer',
  'aws', 'gcloud', 'az', 'wrangler', 'vercel', 'netlify', 'flyctl', 'fly', 'heroku', 'kubectl', 'helm',
]);
/** Subcommands that send something out or publish it. */
const OUTWARD_SUBCOMMANDS = new Map<string, Set<string>>([
  ['git', new Set(['push'])],
  ['npm', new Set(['publish', 'unpublish', 'login', 'adduser', 'dist-tag', 'deprecate', 'owner', 'access', 'token'])],
  ['pnpm', new Set(['publish'])],
  ['yarn', new Set(['publish', 'npm'])],
  ['bun', new Set(['publish'])],
  ['cargo', new Set(['publish', 'login', 'yank', 'owner'])],
  ['gem', new Set(['push', 'owner', 'yank'])],
  ['docker', new Set(['push', 'login'])],
  ['twine', new Set(['upload'])],
  ['poetry', new Set(['publish'])],
]);
const HTTP_PROGRAMS = new Set(['curl', 'wget', 'invoke-webrequest', 'iwr', 'invoke-restmethod', 'irm', 'http', 'xh']);
/**
 * An HTTP call that sends a body or changes something. curl and wget flags are case-sensitive
 * (curl -f fails on errors, -F posts a form; -d sends data, -D saves headers); PowerShell's are not.
 */
const SENDS = /(?:^|\s)(?:-X\s*["']?(?:POST|PUT|PATCH|DELETE|post|put|patch|delete)\b|--(?:request|method)[=\s]+["']?(?:POST|PUT|PATCH|DELETE|post|put|patch|delete)\b|-d\b|--data(?:-[a-z]+)?\b|-F\b|--form\b|-T\b|--upload-file\b|--json\b|--post-(?:data|file)\b)/;
const SENDS_PS = /(?:^|\s)(?:-method\s+["']?(?:post|put|patch|delete)\b|-body\b|-infile\b)/;
/** gh subcommands that only read. */
const GH_READS = new Map<string, Set<string>>([
  ['pr', new Set(['view', 'list', 'status', 'checks', 'diff'])],
  ['issue', new Set(['view', 'list', 'status'])],
  ['run', new Set(['view', 'list', 'watch'])],
  ['repo', new Set(['view'])],
  ['release', new Set(['view', 'list'])],
  ['auth', new Set(['status'])],
]);

/** `gh api` sends fields (-f, -F, --field, --raw-field, --input) or a method other than GET. */
function ghApiWrites(args: string[]): boolean {
  for (let i = 0; i < args.length; i++) {
    const a = args[i]!;
    if (/^(?:-f|-F|--field|--raw-field|--input)(?:=|$)/.test(a) || /^-[fF]./.test(a)) return true;
    let method: string | undefined;
    if (a === '-X' || a === '--method') method = args[i + 1] ?? '';
    else if (a.startsWith('--method=')) method = a.slice('--method='.length);
    else if (a.startsWith('-X')) method = a.slice(2);
    if (method !== undefined && asciiLower(method) !== 'get') return true;
  }
  return false;
}

/** It leaves this machine or cannot be undone: a person decides (default:outward). */
function isOutwardCommand(part: string): boolean {
  const { program, args } = programOf(part);
  const p = program.toLowerCase();
  if (OUTWARD_PROGRAMS.has(p)) return true;
  if (p === 'gh') {
    const [group, verb] = args.filter((a) => !a.startsWith('-'));
    // `gh api <path>` is a GET unless it sends fields or names another method.
    if (group === 'api') return ghApiWrites(args);
    return !(group && verb && GH_READS.get(group)?.has(verb));
  }
  if (HTTP_PROGRAMS.has(p)) return !onlyLoopback(part) && (SENDS.test(part) || SENDS_PS.test(asciiLower(part)));
  const subs = OUTWARD_SUBCOMMANDS.get(p);
  if (!subs) return false;
  const sub = subcommandOf(p, args);
  return !!sub && subs.has(sub.toLowerCase());
}

/** Tools a project runs from its own node_modules: `npx vitest` is the local loop, not a download. */
const LOCAL_BINS = new Set([
  'vitest', 'tsc', 'tsx', 'vite', 'playwright', 'eslint', 'prettier', 'biome', 'turbo', 'jest', 'mocha', 'ts-node', 'esbuild',
  'rollup', 'webpack', 'next', 'astro', 'storybook', 'changeset', 'lint-staged', 'nx', 'lerna', 'knip', 'oxlint', 'stylelint',
  'typedoc', 'c8', 'nyc', 'wireit', 'concurrently', 'rimraf', 'cross-env', 'tailwindcss', 'postcss', 'svelte-kit', 'nuxt',
]);
const RUNNERS = new Set(['npx', 'pnpx', 'bunx']);
/** git that only brings changes in. */
const INBOUND_GIT = new Set(['fetch', 'pull', 'ls-remote', 'submodule', 'clone']);
/** Installs that take what the lockfile says when they name no package. */
const LOCKFILE_INSTALL = new Map<string, Set<string>>([
  ['npm', new Set(['ci', 'install', 'i'])],
  ['pnpm', new Set(['install', 'i'])],
  ['yarn', new Set(['install'])],
  ['bun', new Set(['install', 'i'])],
]);

/** The words after a subcommand that are not options: the packages an install names. */
function namesAfter(args: string[], sub: string): string[] {
  const at = args.findIndex((a) => a.toLowerCase() === sub);
  return at < 0 ? [] : args.slice(at + 1).filter((a) => !a.startsWith('-'));
}

/** The local build and test loop, though it looks like network use. */
function isLocalLoop(part: string): boolean {
  const { program, args } = programOf(part);
  const p = program.toLowerCase();
  const sub = subcommandOf(p, args)?.toLowerCase();
  if (RUNNERS.has(p) || ((p === 'npm' || p === 'pnpm') && sub === 'exec')) {
    // A version, a package option or --yes means a download; a bare known tool is the project's own.
    const rest = RUNNERS.has(p) ? args : args.slice(args.findIndex((a) => a.toLowerCase() === 'exec') + 1);
    const at = rest.findIndex((a) => !a.startsWith('-'));
    // Only the runner's own options count: `npx tsc -p x` passes -p to tsc.
    if (at < 0 || rest.slice(0, at).some((a) => /^(?:-y|--yes|-p|--package)(?:=|$)/.test(a))) return false;
    return LOCAL_BINS.has(rest[at]!.toLowerCase());
  }
  if (p === 'git') return !!sub && INBOUND_GIT.has(sub);
  const lock = LOCKFILE_INSTALL.get(p);
  if (lock) return !!sub && lock.has(sub) && namesAfter(args, sub).length === 0;
  if (p === 'gh') return !isOutwardCommand(part);
  if (HTTP_PROGRAMS.has(p)) return onlyLoopback(part);
  // Network only by a URL it names, and every URL is this machine (a script given localhost).
  return !programReachesNetwork(part) && onlyLoopback(part);
}

/** An install that adds a package the project did not have (npm install x, pnpm add -D y, pip install z). */
function isDependencyInstall(part: string): boolean {
  const { program, args } = programOf(part);
  const p = program.toLowerCase();
  const sub = subcommandOf(p, args)?.toLowerCase();
  if (!sub) return false;
  if (['npm', 'pnpm', 'yarn', 'bun'].includes(p)) return ['install', 'i', 'add'].includes(sub) && namesAfter(args, sub).length > 0;
  if (['pip', 'pip3', 'uv'].includes(p)) return ['install', 'add'].includes(sub) && namesAfter(args, sub).length > 0;
  return false;
}

export type NetworkTier = 'none' | 'local' | 'grey' | 'outward';

/** How far one simple command reaches (ADR 0101). */
export function networkTier(part: string): NetworkTier {
  if (isOutwardCommand(part)) return 'outward';
  if (!isNetworkCommand(part)) return 'none';
  return isLocalLoop(part) ? 'local' : 'grey';
}

/** This app's own tools (the task list, panes, inline visuals): they act on the app only. */
const FIRST_PARTY_MCP = new Set(['ccd_turn', 'ccd_view', 'visualize']);
/** A tool name that starts with one of these words and has none of WRITE_WORDS only reads. */
const READ_WORDS = new Set(['get', 'list', 'read', 'search', 'find', 'query', 'describe', 'count', 'view', 'tabs', 'preview']);
const WRITE_WORDS = new Set([
  'write', 'update', 'delete', 'remove', 'create', 'set', 'send', 'post', 'put', 'replace', 'move', 'add', 'edit', 'upload',
  'publish', 'run', 'exec', 'execute', 'click', 'type', 'submit', 'start', 'stop', 'kill', 'install', 'close', 'archive', 'clear',
]);
/** get_page_text, GetForecast, tabs_context: its words, lower case. */
function nameWords(name: string): string[] {
  const out: string[] = [];
  let w = '';
  for (const ch of name) {
    const sep = ch === '_' || ch === '-' || ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r';
    if (sep || (ch >= 'A' && ch <= 'Z' && w)) {
      if (w) out.push(w.toLowerCase());
      w = '';
    }
    if (!sep) w += ch;
  }
  if (w) out.push(w.toLowerCase());
  return out;
}
/** The desktop app's built-in browser pane: separate from the person's own browser. */
const BROWSER_PANE = 'claude_browser';

export type McpTier = 'local' | 'pane' | 'outward';

/** What an MCP tool may reach: this app or a read (local), the browser pane, or another system. */
export function mcpTier(tool: string): McpTier {
  const m = /^mcp__(.+?)__(.+)$/.exec(tool);
  if (!m) return 'outward';
  const server = m[1]!.toLowerCase();
  const name = m[2]!;
  // ccd_*: the desktop app's own tools; it shows its own card before any of them acts for the person.
  if (FIRST_PARTY_MCP.has(server) || server.startsWith('ccd_')) return 'local';
  const w = nameWords(name);
  if (w.length && READ_WORDS.has(w[0]!) && !w.some((x) => WRITE_WORDS.has(x))) return 'local';
  return server === BROWSER_PANE ? 'pane' : 'outward';
}

/** OS scratch folders: writing there is not writing into anyone's files. */
const SCRATCH = /^(?:\/tmp\/|\/var\/tmp\/|\/private\/tmp\/|\$\{?tmpdir\}?[\\/]|%temp%[\\/]|%tmp%[\\/]|\$env:te?mp[\\/])\S|[\\/]appdata[\\/]local[\\/]temp[\\/]\S/;
const isScratch = (p: string): boolean => SCRATCH.test(asciiLower(p.trim().replace(/^["']|["']$/g, '')));

const DEVICE_SINKS = /^(\/dev\/(null|stdout|stderr|tty)|nul|NUL|\$null|con)$/;

/** Path-looking targets of a command: redirections, and arguments of programs that write. */
function writeTargets(part: string): string[] {
  const out: string[] = [];
  // Redirections: > file, >> file, 2> file (not >&2).
  const re = /(?:^|[^<>&0-9])\d?>{1,2}(?!&)\s*("[^"]*"|'[^']*'|[^\s;|&]+)/g;
  for (let m = re.exec(part); m; m = re.exec(part)) out.push(m[1]!.replace(/^["']|["']$/g, ''));
  const { program, args } = programOf(part);
  const p = program.toLowerCase();
  if (WRITE_PROGRAMS.has(p) || (p === 'sed' && args.some((a) => /^-[a-z]*i/.test(a)))) {
    for (const a of args) if (!a.startsWith('-') && a.length > 0) out.push(a);
  }
  return out.filter((t) => !DEVICE_SINKS.test(t));
}

function writesOutside(part: string, cwd: string | undefined): boolean {
  for (const t of writeTargets(part)) {
    const looksLikePath = isAbsolutePath(t) || t.startsWith('..') || t.startsWith('$HOME') || t.startsWith('%');
    if (!looksLikePath || isScratch(t)) continue;
    if (!cwd || !isAbsolutePath(t) || t.startsWith('~')) return true;
    if (!isInside(t, cwd)) return true;
  }
  return false;
}

const CATASTROPHIC: RegExp[] = [
  // rm -rf / , rm -rf /* , rm -rf ~ , rm -rf $HOME , rm -rf C:\ (any flag order)
  /^(?:sudo\s+)?rm\s+(?:-[a-zA-Z]*\s+)*-[a-zA-Z]*[rR][a-zA-Z]*\s+(?:-[a-zA-Z]*\s+)*(?:--no-preserve-root\s+)?["']?(?:\/\*?|~\/?\*?|\$HOME\/?\*?|[A-Za-z]:[\\/]?\*?)["']?\s*$/,
  /^(?:sudo\s+)?mkfs(?:\.\w+)?\b/,
  /^(?:sudo\s+)?dd\b.*\bof=\/dev\/(?:sd|nvme|hd|disk)/,
  /:\(\)\s*\{\s*:\|:&\s*\};:/,
  /^format(?:\.com)?\s+[A-Za-z]:/i,
  /^remove-item\b.*-recurse\b.*\s["']?[A-Za-z]:[\\/]?["']?(?:\s|$)/i,
];

function isCatastrophic(part: string): boolean {
  return CATASTROPHIC.some((re) => re.test(part.trim()));
}

const at = 0;
const rule = (id: string, decision: PolicyRule['decision'], reason: string, match: PolicyRule['match']): PolicyRule => ({
  id,
  scope: { kind: 'org' },
  match,
  decision,
  reason,
  createdBy: 'orgus',
  createdAt: at,
});

/** The defaults as rules, for display (Settings, Policies). Evaluation uses defaultDecision. */
/** The default a person always sees, whoever answers the rest (ADR 0101). */
export const OUTWARD_RULE_ID = 'default:outward';

export const DEFAULT_RULES: readonly PolicyRule[] = [
  rule('default:destructive', 'deny', 'This command can only destroy data outside any project (for example rm -rf /).', { tool: 'Bash', pattern: 'rm -rf /' }),
  rule(OUTWARD_RULE_ID, 'ask', 'It leaves this machine or cannot be undone, so a person decides.', { pattern: 'git push, publish, deploy, ssh, a write to another service, ...' }),
  rule('default:network', 'ask', 'It downloads or runs something from the internet, so Orgus or a person decides.', { pattern: 'npm install <package>, npx <package>, curl https://..., WebFetch, ...' }),
  rule('default:outside-worktree', 'ask', 'It writes outside the session folder, so Orgus or a person decides.', { pattern: 'paths outside the worktree' }),
  rule('default:mcp', 'ask', 'It acts in the built-in browser pane, so Orgus or a person decides.', { tool: 'mcp__Claude_Browser__*' }),
  rule('default:read-only', 'allow', 'It only reads, searches or plans.', { tool: 'Read, Glob, Grep, Task, ...' }),
  rule('default:local', 'allow', 'It is the local build and test loop, or this app\'s own tools.', { pattern: 'npx vitest, pnpm install, git fetch, curl localhost, ...' }),
  rule('default:in-worktree', 'allow', 'It works inside the session folder and does not touch the network.', { tool: '*' }),
  rule('default:actions', 'allow', 'Orgus actions are confirmed by the person who starts them.', { action: 'session.start' }),
];

const byId = (id: string): PolicyRule => DEFAULT_RULES.find((r) => r.id === id)!;
const result = (id: string): PolicyResult => {
  const r = byId(id);
  return { decision: r.decision, ruleId: r.id, reason: r.reason };
};

/** What the built-in defaults say about one request (one simple command for shells). */
export function defaultDecision(req: PolicyRequest): PolicyResult {
  if (!req.tool) return result('default:actions');
  const tool = req.tool.toLowerCase();
  const subject = req.subject ?? '';
  if (SHELL_TOOLS.has(tool)) {
    if (isCatastrophic(subject)) return result('default:destructive');
    const reach = networkTier(subject);
    if (reach === 'outward') return result(OUTWARD_RULE_ID);
    if (reach === 'grey') return result('default:network');
    if (writesOutside(subject, req.cwd)) return result('default:outside-worktree');
    return result(reach === 'local' ? 'default:local' : 'default:in-worktree');
  }
  if (tool.startsWith('mcp__')) {
    const t = mcpTier(req.tool);
    return result(t === 'local' ? 'default:local' : t === 'pane' ? 'default:mcp' : OUTWARD_RULE_ID);
  }
  // Reading the web: a grey area Orgus allows when it answers (ADR 0101); people who chose to answer see it.
  if (NETWORK_TOOLS.has(tool)) return result('default:network');
  if (FILE_WRITE_TOOLS.has(tool)) {
    if (!subject) return result('default:outside-worktree');
    if (isScratch(subject)) return result('default:in-worktree');
    if (!req.cwd) return isAbsolutePath(subject) ? result('default:outside-worktree') : result('default:in-worktree');
    return isInside(subject, req.cwd) ? result('default:in-worktree') : result('default:outside-worktree');
  }
  if (READ_ONLY_TOOLS.has(tool)) return result('default:read-only');
  return result('default:in-worktree');
}

/** Exposed for risk scoring. */
export const _internal = { isNetworkCommand, writesOutside, isCatastrophic, writeTargets, isDependencyInstall, isScratch };

/** Every simple command in a shell subject (or the subject itself for other tools). */
export function partsOf(req: PolicyRequest): string[] {
  if (req.tool && SHELL_TOOLS.has(req.tool.toLowerCase()) && req.subject) {
    const parts = splitCommand(req.subject);
    return parts.length ? parts : [req.subject];
  }
  return [req.subject ?? ''];
}

